import { PageTemplateMapping, TemplateGroups } from '@ekkogmbh/apisdk';
import { useCallback, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';

interface Context {
  templateGroups: TemplateGroups;
  templateGroupNames: string[];
  setTemplateGroups: (groups: TemplateGroups) => void;
  setTemplateGroup: (name: string, group: PageTemplateMapping) => void;
  removeTemplateGroup: (name: string) => void;
  setPage: (groupName: string, page: string, templateName: string) => void;
  removePage: (groupName: string, page: string) => void;
}
const [TemplateGroupsContext, useTemplateGroups] = createContextHardened<Context>('Template Groups');
export default useTemplateGroups;

interface Props {
  children: React.ReactNode;
}
export const TemplateGroupsProvider = ({ children }: Props): React.JSX.Element => {
  const [templateGroups, setTemplateGroups] = useState<TemplateGroups>({});
  const templateGroupNames = useMemo(() => Object.keys(templateGroups), [templateGroups]);

  const setTemplateGroup = useCallback(
    (name: string, group: PageTemplateMapping) => {
      setTemplateGroups({
        ...templateGroups,
        [name]: group,
      });
    },
    [templateGroups],
  );

  const removeTemplateGroup = useCallback(
    (name: string) => {
      if (templateGroupNames.includes(name)) {
        delete templateGroups[name];
        setTemplateGroups({ ...templateGroups });
      }
    },
    [templateGroups, templateGroupNames],
  );

  const setPage = useCallback(
    (groupName: string, page: string, templateName: string) => {
      setTemplateGroups({
        ...templateGroups,
        [groupName]: {
          ...(templateGroups[groupName] ?? {}),
          [page]: templateName,
        },
      });
    },
    [templateGroups],
  );

  const removePage = useCallback(
    (groupName: string, page: string) => {
      if (templateGroupNames.includes(groupName)) {
        const group = templateGroups[groupName];
        delete group[page];
        setTemplateGroup(groupName, { ...group });
      }
    },
    [templateGroups, templateGroupNames],
  );

  return (
    <TemplateGroupsContext.Provider
      value={{
        templateGroups,
        templateGroupNames,
        setTemplateGroups,
        setTemplateGroup,
        removeTemplateGroup,
        setPage,
        removePage,
      }}
    >
      {children}
    </TemplateGroupsContext.Provider>
  );
};
